import type { ProductStatus } from "@/content/schema";

/** UI strings (product texts come from the API and steps.json). */
export const fr = {
	appTitle: "Montage 3D",
	loading: "Chargement du meuble",
	loadingDetail: "Modèle 3D et étapes d’après la notice",
	loadError: "Le modèle n’a pas pu être chargé",
	intro: "Introduction",
	done: "Terminé",
	stepOf: (n: number, total: number) => `Étape ${n} sur ${total}`,
	progress: (n: number, total: number) => `Étape ${n} / ${total}`,
	state: {
		ready: "prêt",
		playing: "en cours",
		paused: "en pause",
		done: "geste terminé",
	} as const,
	play: "Voir le geste",
	pause: "Pause",
	resume: "Reprendre",
	replay: "Revoir le geste",
	prev: "Étape précédente (←)",
	next: "Étape suivante (→)",
	recenter: "Recentrer",
	recenterTitle: "Recadrer la vue sur cette étape (touche R)",
	touchHint: "1 doigt : tourner · 2 doigts : zoom",
	steps: "Étapes",
	parts: "Pièces de cette étape",
	noParts: "Aucune nouvelle pièce : manipulation du meuble.",
	click: "clic !",
	scrub: "Position dans l’étape",
	speed: "Vitesse",
	hint: "Glisser : tourner · molette : zoom · clic droit : déplacer · ← → étapes · espace lecture · R recentrer",
	stepLabel: { intro: "Intro", end: "Fin" },
	seconds: (s: number) => `${s.toFixed(1).replace(".", ",")} s`,

	// catalogue and search
	catalog: "Catalogue",
	catalogTitle: "Guides de montage 3D",
	catalogIntro:
		"Les meubles IKEA les plus montés, animés pièce par pièce d’après leur notice.",
	searchLabel: "Rechercher un meuble",
	searchPlaceholder: "Nom, désignation ou référence…",
	searching: "Recherche…",
	noResults: "Aucun meuble ne correspond à cette recherche.",
	resultCount: (n: number) => (n > 1 ? `${n} meubles` : `${n} meuble`),
	openGuide: "Ouvrir le guide 3D",
	catalogError: "Le catalogue n’a pas pu être chargé",
	searchLimited: "Trop de recherches d’affilée : patientez un instant.",
	productNotFound: "Ce meuble n’est pas dans le catalogue",
	missingFiles: "Les fichiers du guide 3D sont introuvables.",

	// generation of the 3D guide
	generationStatus: {
		pending: "Non généré",
		queued: "En file d’attente",
		fetching_guide: "Récupération de la notice",
		analyzing_guide: "Lecture de la notice",
		modeling: "Modélisation 3D",
		checking_collisions: "Contrôle des collisions",
		exporting: "Export du modèle",
		publishing: "Publication",
		ready: "Guide 3D prêt",
		failed: "Échec",
		unknown: "Statut inconnu",
	} satisfies Record<ProductStatus, string>,
	generationPending:
		"Le guide de montage 3D de ce meuble n’a pas encore été généré.",
	generationRunning:
		"Le guide de montage 3D de ce meuble est en cours de génération.",
	generationFailed: "La génération du guide de montage 3D a échoué.",
	generationStep: (n: number, total: number) => `Étape ${n} sur ${total}`,
	ikeaGuide: "Notice IKEA (PDF)",
	ikeaPage: "Fiche produit IKEA",
	// navigation and theme
	home: "Accueil",
	signIn: "Se connecter",
	signOut: "Se déconnecter",
	account: "Compte",
	theme: { label: "Thème", light: "Clair", system: "Système", dark: "Sombre" },

	// landing page
	landing: {
		eyebrow: "Montage guidé en 3D",
		title: "Montez votre meuble, sereinement.",
		lead: "Cherchez votre meuble IKEA, puis suivez chaque geste en 3D, à votre rythme. Pas de notice à déchiffrer, pas de pièce oubliée.",
		search: "Chercher",
		examples: "Par exemple :",
		exampleQueries: ["BEKVÄM", "KALLAX", "601.788.87"],
		howTitle: "Trois gestes, et c’est monté",
		how: [
			{
				title: "Cherchez",
				text: "Le nom ou la référence de votre meuble suffit.",
			},
			{
				title: "Suivez",
				text: "Chaque étape est animée pièce par pièce, d’après la notice.",
			},
			{
				title: "Montez",
				text: "Avancez à votre rythme ; revenez en arrière quand vous voulez.",
			},
		],
		popular: "Les plus montés",
		membersTitle: "Le catalogue est réservé aux membres",
		membersText:
			"Connectez-vous, sans mot de passe, pour parcourir les guides de montage 3D.",
		allCatalog: "Tout le catalogue",
		disclaimer: "Guides non officiels, générés d’après les notices IKEA.",
		privacy: "Confidentialité",
	},

	// login page and magic link
	login: {
		title: "Bon retour.",
		lead: "Connectez-vous pour parcourir le catalogue et ouvrir les guides de montage 3D.",
		google: "Continuer avec Google",
		apple: "Continuer avec Apple",
		orMail: "ou par e-mail",
		mailLabel: "Adresse e-mail",
		mailPlaceholder: "vous@exemple.fr",
		mailInvalid: "Cette adresse ne semble pas valide.",
		sendLink: "Recevoir un lien de connexion",
		sending: "Envoi…",
		sent: (mail: string) =>
			`Lien envoyé à ${mail}. Regardez votre boîte de réception.`,
		resend: "Renvoyer le lien",
		otherMail: "Utiliser une autre adresse",
		sendError: "Le lien n’a pas pu être envoyé. Réessayez dans un instant.",
		wait: (s: number) => `Patientez ${s} s avant de redemander un lien.`,
		// `?error=` of a failed Google / Apple sign-in (core/src/core/routers/auth.py)
		errors: {
			cancelled: "Connexion annulée.",
			unverified:
				"Votre adresse e-mail n’est pas vérifiée chez ce fournisseur : recevez plutôt un lien par e-mail.",
			inactive: "Ce compte est désactivé.",
			google: "La connexion avec Google a échoué. Réessayez.",
			apple: "La connexion avec Apple a échoué. Réessayez.",
		} as Record<string, string>,
		failed: "La connexion a échoué. Réessayez.",
		legal:
			"En continuant, vous acceptez les conditions d’utilisation. Aucun mot de passe : vous recevrez un lien par e-mail.",
	},
	verify: {
		title: "Presque connecté.",
		lead: "Confirmez pour ouvrir votre session sur cet appareil.",
		submit: "Se connecter",
		pending: "Connexion…",
		invalid: "Ce lien a expiré ou a déjà servi.",
		missing: "Ce lien de connexion est incomplet.",
		again: "Recevoir un nouveau lien",
	},

	// rate limits (core: RATE_LIMIT_GUIDE)
	guideLimited: {
		title: "Un guide à la fois",
		text: (s: number) =>
			`Vous avez ouvert un autre guide il y a moins d’une minute. Celui-ci s’ouvrira tout seul dans ${s} s.`,
	},
};
export type Strings = typeof fr;
